import { FC, useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Divider } from 'antd';

import { menuConfig } from 'src/constants/menu';
import { MenuSection } from './MenuSection';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { twJoin, twMerge } from '@dynamika-shared/utils/twMerge';
import { getRandomColor } from 'src/utils/getRandomColor';
import Collapse from '@dynamika-shared/ui/Collapse/Collapse';
import { ClinicalTrialAffiliatesType } from '@dynamika-shared/types/clinicalTrialAffiliates';
import { ClinicalTrialList } from 'src/components/LoginSelections/ClinicalTrialList/ClinicalTrialList';
import { useClinicalTrialAffiliatesSlice } from '@restore/clinicalTrialAffiliates/clinicalTrialAffiliates.slice';
import { routesPath } from 'src/router/routes';

type MenuProps = {
  sidebarCollapsed: boolean;
};
export const Menu: FC<MenuProps> = ({ sidebarCollapsed }) => {
  const userData = useAuthSlice(s => s.data);
  const params = useParams();
  const navigate = useNavigate();

  const [selectedTrial, setSelectedTrial] =
    useState<ClinicalTrialAffiliatesType>();
  const [isAuditTrailsAvailable, setIsAuditTrailsAvailable] =
    useState<boolean>(false);

  const [dataCT, loading, fetchClinicalTrials] =
    useClinicalTrialAffiliatesSlice((state, actions) => [
      state.data,
      state.loading,
      actions.fetchClinicalTrialAffiliates,
    ]);

  const initialSelectedCT = useMemo(() => {
    const initialTrial = dataCT.find(ct => ct.id === params.id);

    return selectedTrial ?? initialTrial;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCT, selectedTrial]);

  useEffect(() => {
    if (userData?.selectedRole === 'SPONSOR' && !dataCT?.length) {
      fetchClinicalTrials(null);
    }

    if (userData?.selectedRole) {
      const auditTrailsTabExists = menuConfig[userData?.selectedRole]?.find(
        item => item.name === 'auditTrials'
      );

      auditTrailsTabExists &&
        setIsAuditTrailsAvailable(
          auditTrailsTabExists ? !auditTrailsTabExists?.disabled : false
        );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectCT = (ct: ClinicalTrialAffiliatesType) => {
    setSelectedTrial(ct);
    navigate(`${routesPath.sponsorsAuditClinicalTrials}/${ct?.id}`);
  };

  const randomColor = getRandomColor(1);

  if (!userData?.selectedRole) {
    return null;
  }
  return (
    <ul data-testid='Menu' className='mt-3'>
      {userData?.selectedRole === 'SPONSOR' && isAuditTrailsAvailable && (
        <>
          <div
            className={twMerge(
              'px-[1.6rem] pt-[1rem]',
              sidebarCollapsed && 'p-0'
            )}
          >
            <Collapse
              label={
                !sidebarCollapsed ? (
                  <div className='flex items-center overflow-hidden'>
                    <p
                      className={twJoin(
                        'text-1824 mr-1 flex items-center justify-center rounded-full p-2 font-semibold text-black',
                        'h-[40px] w-[40px]',
                        randomColor
                      )}
                    >
                      {initialSelectedCT?.name?.charAt(0)?.toUpperCase()}
                    </p>
                    <p
                      className={twJoin(
                        'truncate text-black',
                        'text-[16px] font-semibold'
                      )}
                    >
                      {initialSelectedCT?.name}
                    </p>
                  </div>
                ) : (
                  ''
                )
              }
              styles={
                sidebarCollapsed ? { header: 'pb-[1rem] justify-center' } : {}
              }
            >
              <ClinicalTrialList
                clinicalTrials={dataCT}
                handleSelectCT={handleSelectCT}
                context='menu'
                loading={loading}
                sidebarCollapsed={sidebarCollapsed}
              />
            </Collapse>
          </div>

          <Divider />
        </>
      )}

      {menuConfig[userData?.selectedRole]?.map((item, i) => (
        <MenuSection
          item={item}
          key={item.name}
          menuIndex={i}
          sideBarCollapsed={sidebarCollapsed}
        />
      ))}
    </ul>
  );
};
